import {
  FIXED_TEXT_BOX_MIN_EM,
  fixedTextBoxHeightMm,
  type DesignBox,
  type DesignLayer,
} from '@pz/scene';
import { round, textLines, type LayerPatch } from './state.js';
import { run } from './commands.js';
import type { DesignerCtx } from './panels.js';

/**
 * Fixed-width text boxes, and the vertical anchor that says which edge of the
 * box stays put when its height changes.
 *
 * The reference editor has two kinds of text object. An AUTO box hugs its own
 * words: type a longer name and the box grows sideways. A FIXED box is a
 * column the shopper sized themselves — the words wrap at its width, and its
 * HEIGHT follows the number of lines that wrapping produced. Dragging a side
 * handle is what turns the first into the second; the "Fiksna širina okvira"
 * switch shows that state and takes it back.
 *
 * The model (`boxMode`, `anchorV`), the `textBox` command, the one-em floor and
 * `fixedTextBoxHeightMm` are all P's, in `@pz/scene`. What lives here is what
 * the two SHELLS need and the platform cannot know: which edge a box grows away
 * from while the shopper drags, how wide a column a side handle just asked for,
 * and how many lines a sentence becomes when there is no renderer yet to say.
 */

export type TextBoxMode = 'auto' | 'fixed';
export type TextAnchorV = 'top' | 'middle' | 'bottom';

/** The renderer's own default when a layer does not carry one. */
const DEFAULT_LINE_SPACING = 1.15;

// ── Reading the fields ───────────────────────────────────────────────────────

export function boxModeOf(layer: DesignLayer | null | undefined): TextBoxMode {
  return layer?.kind === 'text' && layer.boxMode === 'fixed' ? 'fixed' : 'auto';
}

/** True for a text layer whose words wrap at its own box width. */
export function isFixedTextBox(layer: DesignLayer | null | undefined): boolean {
  return boxModeOf(layer) === 'fixed';
}

export function anchorVOf(layer: DesignLayer | null | undefined): TextAnchorV {
  const value = layer?.kind === 'text' ? layer.anchorV : undefined;
  return value === 'top' || value === 'bottom' ? value : 'middle';
}

/**
 * Whether a text layer may be given a wrap width at all.
 *
 * A bent line's box is the chord-and-sagitta of an arc, a vertical line stacks
 * single glyphs, and a grouped layer is transformed with its siblings; in none
 * of them is "width" a column the words are poured into, and the platform
 * refuses the first two outright. So none of them gets side handles.
 */
export function canHaveFixedWidth(layer: DesignLayer | null | undefined): boolean {
  if (!layer || layer.kind !== 'text') return false;
  return !layer.bend && layer.orientation !== 'vertical' && !layer.groupId;
}

/**
 * The narrowest a wrap column may be: one em of this layer's own size.
 *
 * Under an em even a single letter has to break, and the "wrap" becomes a
 * column of characters nobody asked for — which is why `designAreaProblems`
 * refuses it as a blocking `bad-geometry`. The handles clamp to the SAME
 * number, from the same constant, so a drag can never produce a design the
 * validator then complains about.
 */
export function minWrapWidthMm(sizeMm: number): number {
  return round(Math.max(0.5, (sizeMm > 0 ? sizeMm : 1) * FIXED_TEXT_BOX_MIN_EM), 2);
}

// ── The box maths (pure — this is the part the tests pin down) ───────────────

/**
 * The same box at a new height, with the anchored edge left exactly where it
 * was.
 *
 * The shift is computed in the BOX's own frame and only then turned through
 * its rotation: a 30°-rotated column that grows downwards must grow along its
 * own down, not the zone's, or the text would walk sideways every time a word
 * pushed a new line.
 */
export function anchorBoxHeight(box: DesignBox, heightMm: number, anchor: TextAnchorV): DesignBox {
  const h = round(Math.max(0.5, heightMm), 2);
  if (anchor === 'middle') return { ...box, h };
  const grew = h - box.h;
  // Top-anchored: the centre moves DOWN by half of what the box gained, so the
  // top edge does not move. Bottom-anchored: up, by the same half.
  const along = anchor === 'top' ? grew / 2 : -grew / 2;
  const rad = ((box.rotation ?? 0) * Math.PI) / 180;
  return {
    ...box,
    h,
    x: round(box.x - along * Math.sin(rad), 3),
    y: round(box.y + along * Math.cos(rad), 3),
  };
}

/**
 * A side handle dragged to `localX` (millimetres from the box centre, in the
 * box's own unrotated frame): a new WIDTH and the centre that keeps the
 * opposite edge still. The height is not touched and neither is `sizeMm` —
 * that is the whole difference between a side handle and a corner one.
 */
export function sideWidthBox(
  startBox: DesignBox,
  localX: number,
  handle: 'e' | 'w',
  minWidthMm = 1,
): DesignBox {
  const half = startBox.w / 2;
  const dragged = handle === 'e' ? localX + half : half - localX;
  const w = Math.max(minWidthMm, dragged);
  // The dragged edge moves and the opposite one stays, so the centre moves by
  // half the change — towards the handle.
  const along = ((handle === 'e' ? 1 : -1) * (w - startBox.w)) / 2;
  const rad = ((startBox.rotation ?? 0) * Math.PI) / 180;
  return {
    ...startBox,
    w: round(w, 2),
    x: round(startBox.x + along * Math.cos(rad), 3),
    y: round(startBox.y + along * Math.sin(rad), 3),
  };
}

/**
 * How many whole lines a measured ink height is.
 *
 * Expansion hands Skia `heightMultiplier = lineSpacing`, so every line box is
 * exactly one em times the spacing and the measured ink is N of them. Rounding
 * the measurement back to N and asking `fixedTextBoxHeightMm` for the height is
 * not a detour: it turns a figure that can wobble by a fraction of a pixel
 * between renders into the integer the shopper can actually see, which is what
 * keeps a box from creeping a hundredth of a millimetre on every repaint.
 */
export function linesFromHeightMm(heightMm: number, sizeMm: number, lineSpacing = DEFAULT_LINE_SPACING): number {
  const lineBox = sizeMm * (lineSpacing > 0 ? lineSpacing : DEFAULT_LINE_SPACING);
  if (!(lineBox > 0) || !Number.isFinite(heightMm)) return 1;
  return Math.max(1, Math.round(heightMm / lineBox));
}

/**
 * How many lines the text becomes when poured into a column `widthMm` wide.
 *
 * A greedy word wrap, the same rule every paragraph engine uses: a word that
 * does not fit starts a line, and a single word wider than the whole column is
 * broken by characters rather than left to overflow. `measureEm` is the same
 * measurement function the canvas uses for advance widths (Skia where it can,
 * a 2D canvas where it cannot), so this estimate and the real layout disagree
 * only about kerning at the break.
 *
 * It is only ever used before the render bundle has loaded: once Skia is there
 * the canvas asks IT how tall the wrapped paragraph came out.
 */
export function wrapLineCount(
  text: string,
  widthMm: number,
  sizeMm: number,
  tracking: number | undefined,
  measureEm: (line: string) => number,
  uppercase?: boolean,
): number {
  if (!(widthMm > 0) || !(sizeMm > 0)) return Math.max(1, textLines(text, uppercase).length);
  const extra = ((tracking ?? 0) / 1000) * sizeMm;
  const widthOf = (piece: string): number => measureEm(piece) * sizeMm + extra * [...piece].length;
  let total = 0;
  for (const hard of textLines(text, uppercase)) {
    total += wrapOneLine(hard, widthMm, widthOf);
  }
  return Math.max(1, total);
}

function wrapOneLine(line: string, widthMm: number, widthOf: (piece: string) => number): number {
  if (!line.trim()) return 1;
  const words = line.split(/(\s+)/).filter((piece) => piece !== '');
  let lines = 1;
  let current = '';
  for (const word of words) {
    // Trailing spaces never push a break — they hang past the edge, as they do
    // in every text engine.
    if (/^\s+$/.test(word)) {
      current += word;
      continue;
    }
    const candidate = current + word;
    if (!current || widthOf(candidate) <= widthMm) {
      current = candidate;
    } else {
      lines += 1;
      current = word;
    }
    // One word longer than the column: break it until the remainder fits.
    while (widthOf(current) > widthMm && [...current].length > 1) {
      const glyphs = [...current];
      let fit = glyphs.length - 1;
      while (fit > 1 && widthOf(glyphs.slice(0, fit).join('')) > widthMm) fit -= 1;
      lines += 1;
      current = glyphs.slice(fit).join('');
    }
  }
  return lines;
}

/** The height a column of `lines` lines needs — P's line box, never an estimate. */
export function fixedBoxHeightMm(lines: number, sizeMm: number, lineSpacing?: number): number {
  return round(fixedTextBoxHeightMm(lines, sizeMm, lineSpacing), 2);
}

// ── Writing the fields ───────────────────────────────────────────────────────

export interface TextBoxChange {
  boxMode?: TextBoxMode;
  anchorV?: TextAnchorV;
  widthMm?: number;
}

/**
 * The one writer for the two fields, through P's own command.
 *
 * `boxMode` is required by the command even when only the anchor is changing,
 * so it is always filled in from the layer as it stands; `widthMm` is refused
 * outright beside `boxMode: 'auto'` (an auto box is measured, so a width there
 * could never take effect), which is why the callers that turn the switch OFF
 * send no width at all.
 */
export function setTextBox(ctx: DesignerCtx, ids: string[], change: TextBoxChange): boolean {
  if (!ids.length) return false;
  const first = ctx.store.value().layers.find((layer) => ids.includes(layer.id));
  const boxMode = change.boxMode ?? boxModeOf(first);
  return run(ctx, {
    op: 'textBox',
    ids,
    boxMode,
    ...(change.anchorV ? { anchorV: change.anchorV } : {}),
    ...(boxMode === 'fixed' && change.widthMm !== undefined
      ? { widthMm: round(Math.max(minWrapWidthMm(first?.kind === 'text' ? first.sizeMm : 1), change.widthMm), 2) }
      : {}),
  });
}

/**
 * The wrap width, as a control that can be held down.
 *
 * Once a layer IS a column, changing its width changes nothing but `box.w`,
 * so it goes the way every other live control goes: a tagged `update`, which
 * the store coalesces into ONE history entry however many times the shopper
 * presses "Više". Running it through the command each time would push an
 * untagged entry per press — the phone's Otkaži and a desktop Ctrl+Z would
 * then walk back through a stepper one click at a time.
 *
 * Only the FIRST width — the one that makes an auto box a column — is a
 * command, because that is the change the platform has to validate.
 */
export function setTextBoxWidth(ctx: DesignerCtx, layer: DesignLayer, widthMm: number, tag?: string): void {
  if (layer.kind !== 'text') return;
  if (!isFixedTextBox(layer)) {
    setTextBox(ctx, [layer.id], { boxMode: 'fixed', widthMm });
    return;
  }
  const w = round(Math.max(minWrapWidthMm(layer.sizeMm), widthMm), 2);
  if (Math.abs(w - layer.box.w) < 0.005) return;
  ctx.dispatch({
    type: 'update',
    id: layer.id,
    patch: { box: { ...layer.box, w } },
    ...(tag ? { tag } : {}),
  });
}

/**
 * What a side-handle drag has to write besides the new box.
 *
 * Dragging a side handle on an auto text box is what MAKES it a column — the
 * reference editor's own rule, and the reason the switch in Efekti is a
 * read-out rather than a prerequisite. The drag writes the box and the mode in
 * ONE patch because it was one gesture and must be one undo step; the fields
 * it clears are the same ones `normalizeDesignAnswer` clears for a fixed box
 * (a wrapped line has no arc, and its width is the shopper's, not a
 * measurement).
 */
export function fixedByDragPatch(layer: DesignLayer): LayerPatch {
  if (isFixedTextBox(layer)) return {};
  return { boxMode: 'fixed', bend: undefined, textWidthMm: undefined };
}
